import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Trash2, Store, CalendarDays, MapPin, Locate, Sparkles, GripVertical, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import SingleImageUpload from "../../components/admin/SingleImageUpload";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const DEFAULT_HOURS = { mon_sat: { open: "", close: "", closed: false }, sunday: { open: "", close: "", closed: false } };
const EMPTY = {
  name: "", slug: "", description: "",
  address_line1: "", address_line2: "", town: "", city_county: "", postcode: "",
  phone: "", email: "",
  collection_instructions: "",
  google_maps_url: "", google_maps_embed_url: "", map_verified: false, latitude: "", longitude: "",
  opening_hours: DEFAULT_HOURS,
  primary_logo: "", secondary_logo: "", featured_image: "",
  services: [], gallery: [],
  is_active: true, is_public: false, is_featured: false, is_collection_point: true,
  localbusiness_enabled: false, display_order: 10,
  seo_title: "", seo_description: "",
};

// Mirror of backend _map_embed_url priority: embed URL > name+address > coords.
function buildEmbed(f) {
  const emb = (f.google_maps_embed_url || "").trim();
  if (emb) return emb;
  const addr = [f.address_line1, f.address_line2, f.town, f.city_county, f.postcode].filter((x) => (x || "").trim()).join(", ");
  if (addr) {
    const q = [f.name, addr, "United Kingdom"].filter(Boolean).join(", ");
    return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
  }
  const lat = Number(f.latitude), lng = Number(f.longitude);
  if (f.latitude !== "" && f.longitude !== "" && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180)
    return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  return null;
}

// Client mirror of the backend LocalBusiness requirements.
function lbMissing(f) {
  const m = [];
  const hasHours = (() => {
    const ms = f.opening_hours?.mon_sat || {}, su = f.opening_hours?.sunday || {};
    return (ms.closed || (ms.open && ms.close)) || (su.closed || (su.open && su.close));
  })();
  if (!f.name?.trim()) m.push("Store name");
  if (!f.address_line1?.trim()) m.push("Address line 1");
  if (!f.town?.trim()) m.push("Town");
  if (!f.postcode?.trim()) m.push("Postcode");
  if (!f.phone?.trim()) m.push("Phone number");
  if (!(Number(f.latitude) >= -90 && Number(f.latitude) <= 90 && f.latitude !== "")) m.push("Valid latitude");
  if (!(Number(f.longitude) >= -180 && Number(f.longitude) <= 180 && f.longitude !== "")) m.push("Valid longitude");
  if (!hasHours) m.push("At least one opening-hours entry");
  if (!f.primary_logo?.trim()) m.push("Primary logo");
  return m;
}

function BuyingPeriodsCard() {
  const [periods, setPeriods] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/collection/periods").then(({ data }) => setPeriods(data.selling_periods)).catch(() => {}); }, []);
  const setP = (i, patch) => setPeriods((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const mdToDate = (md) => `2000-${md}`;
  const dateToMd = (val) => (val || "").slice(5) || "";
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/collection/periods", { selling_periods: periods });
      setPeriods(data.selling_periods);
      toast.success("Buying & collection dates updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!periods) return null;
  return (
    <GlassCard className="p-6" hover={false} data-testid="buying-periods-card">
      <div className="flex items-center gap-3">
        <div className="grid place-items-center h-10 w-10 rounded-lg bg-gold/10 border border-gold/20"><CalendarDays className="h-5 w-5 text-gold" /></div>
        <div>
          <h2 className="font-cinzel text-xl text-white">Fireworks Buying &amp; Collection Dates</h2>
          <p className="text-sm text-muted-foreground">The two permitted selling periods (recur annually). Amend the dates if licensing changes — you cannot add or remove periods.</p>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {periods.map((p, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3" data-testid={`buying-period-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-white/50">Period {i + 1}</span>
              <div className="flex items-center gap-2">
                <Switch data-testid={`period-enabled-${i}`} checked={!!p.enabled} onCheckedChange={(v) => setP(i, { enabled: v })} />
                <span className={p.enabled ? "text-emerald-400 text-xs" : "text-white/40 text-xs"}>{p.enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/70 text-xs">Start</Label><Input data-testid={`period-start-${i}`} type="date" value={mdToDate(p.start)} onChange={(e) => setP(i, { start: dateToMd(e.target.value) })} className={`${inp} [color-scheme:dark]`} /></div>
              <div><Label className="text-white/70 text-xs">End</Label><Input data-testid={`period-end-${i}`} type="date" value={mdToDate(p.end)} onChange={(e) => setP(i, { end: dateToMd(e.target.value) })} className={`${inp} [color-scheme:dark]`} /></div>
            </div>
            <p className="text-[11px] text-white/40">The year is ignored — only the day &amp; month are used, so each period repeats every year.</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid="save-periods-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Dates</Button>
      </div>
    </GlassCard>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3">
      <div><Label className="text-white/85">{label}</Label>{hint && <p className="text-xs text-white/50 mt-0.5">{hint}</p>}</div>
      <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}

function HoursRow({ label, value, onChange, testid }) {
  const v = value || { open: "", close: "", closed: false };
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <Label className="text-white/85">{label}</Label>
        <label className="flex items-center gap-2 text-xs text-white/60"><Switch checked={!!v.closed} onCheckedChange={(c) => onChange({ ...v, closed: c })} data-testid={`${testid}-closed`} /> Closed</label>
      </div>
      {!v.closed && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div><Label className="text-white/60 text-xs">Opens</Label><Input type="time" value={v.open} onChange={(e) => onChange({ ...v, open: e.target.value })} className={`${inp} [color-scheme:dark]`} data-testid={`${testid}-open`} /></div>
          <div><Label className="text-white/60 text-xs">Closes</Label><Input type="time" value={v.close} onChange={(e) => onChange({ ...v, close: e.target.value })} className={`${inp} [color-scheme:dark]`} data-testid={`${testid}-close`} /></div>
        </div>
      )}
    </div>
  );
}

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Changing any location-affecting field invalidates prior map verification.
  const setMapField = (k, v) => setForm((f) => ({ ...f, [k]: v, map_verified: false }));
  const setHours = (day, val) => setForm((f) => ({ ...f, opening_hours: { ...f.opening_hours, [day]: val } }));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/stores").then(({ data }) => setStores(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      ...EMPTY, ...s,
      latitude: s.latitude ?? "", longitude: s.longitude ?? "",
      opening_hours: { ...DEFAULT_HOURS, ...(s.opening_hours || {}) },
      services: s.services || [], gallery: s.gallery || [],
    });
    setEditingId(s.store_id); setOpen(true);
  };

  const extractCoords = async () => {
    if (!form.google_maps_url?.trim()) { toast.error("Paste a Google Maps Directions URL first"); return; }
    setExtracting(true);
    try {
      const { data } = await api.post("/admin/stores/extract-coords", { url: form.google_maps_url });
      if (data.found) {
        setForm((f) => ({ ...f, latitude: data.latitude, longitude: data.longitude, map_verified: false }));
        toast.warning("Suggested coordinates added — verify the marker on the preview before publishing.");
      } else toast.error("Couldn't read coordinates from that URL — enter them manually.");
    } catch { toast.error("Extraction failed"); }
    finally { setExtracting(false); }
  };

  const addService = () => set("services", [...form.services, { name: "", description: "", image: "", featured: false, enabled: true, display_order: (form.services.length + 1) * 10 }]);
  const setService = (i, patch) => set("services", form.services.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delService = (i) => set("services", form.services.filter((_, j) => j !== i));
  const addGallery = () => set("gallery", [...form.gallery, { image: "", caption: "", featured: false, enabled: true, display_order: (form.gallery.length + 1) * 10 }]);
  const setGallery = (i, patch) => set("gallery", form.gallery.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delGallery = (i) => set("gallery", form.gallery.filter((_, j) => j !== i));

  const missing = lbMissing(form);
  const canEnableLb = missing.length === 0;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Store name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        display_order: Number(form.display_order) || 10,
        latitude: form.latitude === "" ? "" : Number(form.latitude),
        longitude: form.longitude === "" ? "" : Number(form.longitude),
      };
      if (editingId) await api.patch(`/admin/stores/${editingId}`, payload);
      else await api.post("/admin/stores", payload);
      toast.success(editingId ? "Store updated" : "Store created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/stores/${s.store_id}`); toast.success("Store deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete store"); }
  };

  const lbBadge = (s) => {
    const map = { enabled: ["Schema ON", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"], ready: ["Schema ready", "bg-gold/15 text-gold border-gold/30"], incomplete: ["Schema off", "bg-white/10 text-white/50 border-white/15"] };
    const [t, c] = map[s.lb_status] || map.incomplete;
    return <Badge className={`border ${c}`}>{t}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-stores">
      <BuyingPeriodsCard />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-cinzel text-2xl sm:text-3xl text-white tracking-wide">Stores</h1>
          <p className="mt-2 text-muted-foreground">Rich store profiles power the public <span className="text-gold">/stores</span> directory. Lower display order appears first.</p>
        </div>
        <Button data-testid="add-store-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Store</Button>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : stores.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Store className="h-8 w-8 mx-auto text-gold/60" /><p className="mt-3">No stores yet. Create your first collection point.</p></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Store</TableHead>
                <TableHead className="text-white/70">Location</TableHead>
                <TableHead className="text-white/70 text-center">Order</TableHead>
                <TableHead className="text-white/70">Visibility</TableHead>
                <TableHead className="text-white/70">Schema</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map((s) => (
                <TableRow key={s.store_id} className="border-white/10" data-testid={`store-row-${s.store_id}`}>
                  <TableCell className="text-white font-medium">
                    {s.name}{s.key === "primary" && <span className="ml-2 text-[10px] text-gold border border-gold/40 rounded-full px-2 py-0.5">PRIMARY</span>}
                    <div className="text-[11px] text-white/40 font-normal">/stores/{s.slug}</div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{[s.town, s.postcode].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-center text-white/80">{s.display_order ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs">
                      <span className={s.is_active ? "text-emerald-400" : "text-firered"}>{s.is_active ? "Active" : "Disabled"}</span>
                      <span className={s.is_public ? "text-gold" : "text-white/40"}>{s.is_public ? "Public" : "Hidden"}</span>
                    </div>
                  </TableCell>
                  <TableCell>{lbBadge(s)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {s.is_public && s.is_active && <a href={`/stores/${s.slug}`} target="_blank" rel="noreferrer" title="View public page" className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><ExternalLink className="h-4 w-4" /></a>}
                    <button onClick={() => openEdit(s)} title="Edit" data-testid={`edit-store-${s.store_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    {s.key !== "primary" && <button onClick={() => remove(s)} title="Delete" data-testid={`delete-store-${s.store_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-firered/10"><Trash2 className="h-4 w-4" /></button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="store-dialog">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-xl">{editingId ? "Edit Store" : "New Store"}</DialogTitle>
            <DialogDescription className="text-white/50">Rich profile shown on the public store directory and individual store page.</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="mt-2">
            <TabsList className="flex flex-wrap h-auto bg-black/30 border border-white/10">
              {[["general", "General"], ["address", "Address & Map"], ["hours", "Hours"], ["branding", "Branding"], ["services", "Services"], ["gallery", "Gallery"], ["seo", "SEO & Schema"]].map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-gold data-[state=active]:text-ink text-white/70">{l}</TabsTrigger>
              ))}
            </TabsList>

            {/* GENERAL */}
            <TabsContent value="general" className="space-y-4 pt-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="store-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">URL slug</Label><Input data-testid="store-slug-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from name" className={inp} /></div>
              <div><Label className="text-white/80">Description</Label><Textarea data-testid="store-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inp} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <ToggleRow label="Active" hint="Enabled at all" checked={form.is_active} onChange={(v) => set("is_active", v)} testid="store-active-switch" />
                <ToggleRow label="Public" hint="Show on /stores directory" checked={form.is_public} onChange={(v) => set("is_public", v)} testid="store-public-switch" />
                <ToggleRow label="Collection point" checked={form.is_collection_point} onChange={(v) => set("is_collection_point", v)} testid="store-collection-switch" />
                <ToggleRow label="Featured" checked={form.is_featured} onChange={(v) => set("is_featured", v)} testid="store-featured-switch" />
              </div>
              <div><Label className="text-white/80">Display order</Label><Input data-testid="store-order-input" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className={inp} /></div>
            </TabsContent>

            {/* ADDRESS & MAP */}
            <TabsContent value="address" className="space-y-4 pt-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-white/80">Address line 1</Label><Input data-testid="store-address1-input" value={form.address_line1} onChange={(e) => setMapField("address_line1", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/80">Address line 2</Label><Input data-testid="store-address2-input" value={form.address_line2} onChange={(e) => setMapField("address_line2", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/80">Town</Label><Input data-testid="store-town-input" value={form.town} onChange={(e) => setMapField("town", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/80">City / County</Label><Input data-testid="store-county-input" value={form.city_county} onChange={(e) => setMapField("city_county", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/80">Postcode</Label><Input data-testid="store-postcode-input" value={form.postcode} onChange={(e) => setMapField("postcode", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/80">Phone</Label><Input data-testid="store-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></div>
                <div className="sm:col-span-2"><Label className="text-white/80">Email</Label><Input data-testid="store-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></div>
                <div className="sm:col-span-2"><Label className="text-white/80">Collection instructions</Label><Textarea data-testid="store-collection-instructions" value={form.collection_instructions} onChange={(e) => set("collection_instructions", e.target.value)} rows={2} placeholder="e.g. Ask at the Post Office counter and quote your reservation number." className={inp} /></div>
              </div>

              <div>
                <Label className="text-white/80">Google Maps Directions URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="store-maps-input" value={form.google_maps_url} onChange={(e) => set("google_maps_url", e.target.value)} placeholder="Official business listing / navigation link" className="bg-black/30 border-white/15 text-white" />
                  <Button type="button" onClick={extractCoords} disabled={extracting} variant="outline" className="border-white/20 text-white gap-2 shrink-0" data-testid="extract-coords-btn">{extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />} Suggest coords</Button>
                </div>
                <p className="text-xs text-white/45 mt-1.5 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Used by the public “Get Directions” button (opens in a new tab). It does NOT set the embedded map.</p>
              </div>

              <div>
                <Label className="text-white/80">Google Maps Embed URL <span className="text-white/40">(optional)</span></Label>
                <Input data-testid="store-embed-input" value={form.google_maps_embed_url} onChange={(e) => setMapField("google_maps_embed_url", e.target.value)} placeholder="Paste an embeddable maps URL to override the address-based map" className={inp} />
                <p className="text-xs text-white/45 mt-1.5">If blank, the embedded map is generated from the store name + full address (recommended). Verified coordinates are used only as a last resort.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-white/80">Latitude</Label><Input data-testid="store-lat-input" value={form.latitude} onChange={(e) => setMapField("latitude", e.target.value)} placeholder="e.g. 51.8523" className={inp} /></div>
                <div><Label className="text-white/80">Longitude</Label><Input data-testid="store-lng-input" value={form.longitude} onChange={(e) => setMapField("longitude", e.target.value)} placeholder="e.g. 1.1230" className={inp} /></div>
              </div>
              <p className="text-[11px] text-amber flex items-start gap-1.5" data-testid="coords-warning"><MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Coordinates extracted from a Google Maps URL may represent the map centre rather than the exact business location. They feed structured data / future store locator — verify the marker below before publishing.</p>

              {/* Live map preview + verification */}
              <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3" data-testid="map-preview-block">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-white/85 flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> Map preview</Label>
                  {!form.map_verified && <span className="text-[11px] text-amber">Not verified — hidden from the public page</span>}
                  {form.map_verified && <span className="text-[11px] text-emerald-400">Verified — visible publicly</span>}
                </div>
                {buildEmbed(form) ? (
                  <iframe title="Map preview" src={buildEmbed(form)} loading="lazy" className="w-full h-[300px] rounded-lg border-0" data-testid="map-preview-iframe" />
                ) : (
                  <p className="text-sm text-white/50">Add an address, embed URL or coordinates to preview the map.</p>
                )}
                <label className="flex items-center gap-2 text-sm text-white/85">
                  <Switch data-testid="map-verified-switch" checked={!!form.map_verified} disabled={!buildEmbed(form)} onCheckedChange={(v) => set("map_verified", v)} />
                  Map location verified — I have confirmed the marker points to this store.
                </label>
              </div>
            </TabsContent>

            {/* HOURS */}
            <TabsContent value="hours" className="space-y-4 pt-4">
              <HoursRow label="Monday – Saturday" value={form.opening_hours.mon_sat} onChange={(v) => setHours("mon_sat", v)} testid="hours-mon-sat" />
              <HoursRow label="Sunday" value={form.opening_hours.sunday} onChange={(v) => setHours("sunday", v)} testid="hours-sunday" />
              <p className="text-xs text-white/45">Opening hours appear on the public store page and are required for LocalBusiness structured data.</p>
            </TabsContent>

            {/* BRANDING */}
            <TabsContent value="branding" className="space-y-6 pt-4">
              <div><Label className="text-white/80">Primary logo</Label><div className="mt-2"><SingleImageUpload value={form.primary_logo} onChange={(u) => set("primary_logo", u)} testid="store-primary-logo" /></div></div>
              <div><Label className="text-white/80">Secondary logo</Label><div className="mt-2"><SingleImageUpload value={form.secondary_logo} onChange={(u) => set("secondary_logo", u)} testid="store-secondary-logo" /></div></div>
              <div><Label className="text-white/80">Featured / hero image</Label><div className="mt-2"><SingleImageUpload value={form.featured_image} onChange={(u) => set("featured_image", u)} testid="store-featured-image" /></div></div>
            </TabsContent>

            {/* SERVICES */}
            <TabsContent value="services" className="space-y-4 pt-4">
              {form.services.length === 0 && <p className="text-sm text-white/50">No services yet.</p>}
              {form.services.map((sv, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3" data-testid={`service-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1"><GripVertical className="h-3 w-3" /> Service {i + 1}</span>
                    <button onClick={() => delService(i)} className="text-firered/80 hover:text-firered" data-testid={`del-service-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Input value={sv.name} onChange={(e) => setService(i, { name: e.target.value })} placeholder="Service name" className={inp} data-testid={`service-name-${i}`} />
                  <Textarea value={sv.description} onChange={(e) => setService(i, { description: e.target.value })} placeholder="Short description" rows={2} className={inp} />
                  <SingleImageUpload value={sv.image} onChange={(u) => setService(i, { image: u })} testid={`service-image-${i}`} />
                  <div className="flex items-center gap-6 text-xs text-white/70">
                    <label className="flex items-center gap-2"><Switch checked={!!sv.enabled} onCheckedChange={(v) => setService(i, { enabled: v })} /> Enabled</label>
                    <label className="flex items-center gap-2"><Switch checked={!!sv.featured} onCheckedChange={(v) => setService(i, { featured: v })} /> Featured</label>
                  </div>
                </div>
              ))}
              <Button type="button" onClick={addService} variant="outline" className="border-white/20 text-white gap-2" data-testid="add-service-btn"><Plus className="h-4 w-4" /> Add service</Button>
            </TabsContent>

            {/* GALLERY */}
            <TabsContent value="gallery" className="space-y-4 pt-4">
              {form.gallery.length === 0 && <p className="text-sm text-white/50">No gallery images yet.</p>}
              {form.gallery.map((g, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3" data-testid={`gallery-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-white/50">Image {i + 1}</span>
                    <button onClick={() => delGallery(i)} className="text-firered/80 hover:text-firered" data-testid={`del-gallery-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <SingleImageUpload value={g.image} onChange={(u) => setGallery(i, { image: u })} testid={`gallery-image-${i}`} />
                  <Input value={g.caption} onChange={(e) => setGallery(i, { caption: e.target.value })} placeholder="Caption (optional)" className={inp} data-testid={`gallery-caption-${i}`} />
                  <div className="flex items-center gap-6 text-xs text-white/70">
                    <label className="flex items-center gap-2"><Switch checked={!!g.enabled} onCheckedChange={(v) => setGallery(i, { enabled: v })} /> Enabled</label>
                    <label className="flex items-center gap-2"><Switch checked={!!g.featured} onCheckedChange={(v) => setGallery(i, { featured: v })} /> Featured</label>
                  </div>
                </div>
              ))}
              <Button type="button" onClick={addGallery} variant="outline" className="border-white/20 text-white gap-2" data-testid="add-gallery-btn"><Plus className="h-4 w-4" /> Add image</Button>
            </TabsContent>

            {/* SEO & SCHEMA */}
            <TabsContent value="seo" className="space-y-4 pt-4">
              <div><Label className="text-white/80">SEO title</Label><Input data-testid="store-seo-title" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="Defaults to store name" className={inp} /></div>
              <div><Label className="text-white/80">SEO description</Label><Textarea data-testid="store-seo-description" value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={3} className={inp} /></div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div><Label className="text-white/85 flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> LocalBusiness structured data</Label><p className="text-xs text-white/50 mt-0.5">Emits per-store Schema.org JSON-LD on the public page.</p></div>
                  <Switch data-testid="store-lb-switch" checked={!!form.localbusiness_enabled} disabled={!canEnableLb && !form.localbusiness_enabled} onCheckedChange={(v) => set("localbusiness_enabled", v)} />
                </div>
                {!canEnableLb && (
                  <div className="mt-3 text-xs text-amber" data-testid="lb-missing">
                    Complete these before enabling: {missing.join(", ")}.
                  </div>
                )}
                {canEnableLb && !form.localbusiness_enabled && <p className="mt-3 text-xs text-emerald-400">All requirements met — you can enable structured data.</p>}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-store-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
